import { logger } from '../utils/logger';
import { adminApiClient, userApiClient, ApiError } from './client';
import type { RequestOptions } from './client';

/**
 * API Architecture:
 * - api-client.ts: Low-level HTTP client with auth token management
 * - api/: High-level domain API methods organized by entity
 *
 * Current state:
 * - apiFetch: Custom wrapper used by most API methods (duplicates some userApiClient functionality)
 * - Returns properly typed data instead of unknown
 */

// Generic fetch wrapper with proper types - now uses the new simplified API client
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  logger.debug('API fetch:', {
    endpoint,
    method: options.method || 'GET',
    hasBody: options.body !== undefined,
  });

  try {
    // Use the new simplified API client
    const { skipAuth, ...fetchOptions } = options;
    return await userApiClient.request<T>(endpoint, {
      ...fetchOptions,
      skipAuth,
    } as RequestOptions);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Re-throw unknown errors
    throw error;
  }
}

// Admin-flavor fetch wrapper: identical contract to apiFetch but routes
// through adminApiClient (bound to adminSession) so directives and
// session-readiness gating are scoped to the admin domain. Used by
// admin-prefixed lib/api modules that still use the apiFetch shape.
export async function adminApiFetch<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  logger.debug('Admin API fetch:', {
    endpoint,
    method: options.method || 'GET',
    hasBody: options.body !== undefined,
  });

  try {
    const { skipAuth, ...fetchOptions } = options;
    return await adminApiClient.request<T>(endpoint, {
      ...fetchOptions,
      skipAuth,
    } as RequestOptions);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw error;
  }
}

// Public fetch wrapper for unauthenticated requests
export async function publicApiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return apiFetch<T>(endpoint, { ...options, skipAuth: true });
}
